/* Copyright 2013 - 2024 Waiterio LLC */
/** @jsx jsx */
import { css, jsx } from '@emotion/react'
import React, { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import faviconUrl from '@waiterio/shared/faviconUrl.js'
import globoWebsiteId from '@waiterio/shared/globoWebsiteId.js'
import getDocuments from '@globo/client/getDocuments.js'
import useDocuments from '@globo/react/useDocuments.js'
import { useTranslation } from '@multilocale/react/index.js'
import debounce from '@monorepo/shared/debounce.js'
import throttle from '@monorepo/shared/throttle.js'
import IconSearch from '@stiloso/icons/IconSearch.js'
import IconClose from '@stiloso/icons/IconClose.js'
import Layout from '../components/LayoutComponent.js'
import languages from '../languages.js'

const jumbotron = css({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  backgroundColor: 'var(--color-primary)',
  height: 300,
  padding: '0px 25px',
})

const searchBar = css({
  cursor: 'text',
  boxShadow: '0px 1.5px 4px rgba(0,0,0,0.24)',
  borderRadius: 30,
  backgroundColor: 'white',
  display: 'flex',
  position: 'relative',
  justifyContent: 'center',
  alignItems: 'center',
  maxWidth: 350,
  width: '100%',
})

const searchInput = css({
  outline: 'none',
  border: 0,
  borderRadius: 30,
  fontSize: 16,
  padding: 20,
  maxWidth: 200,
})

const documentLink = css({
  backgroundColor: 'white',
  cursor: 'pointer',
  display: 'block',
  maxWidth: 500,
  margin: '20px auto',
  boxShadow:
    'rgba(0, 0, 0, 0.24) 0px 1.5px 4px, rgba(0, 0, 0, 0.19) 0px 0.5px 2px',
  padding: 18,
  '&:hover': {
    backgroundColor: '#c2e9f7',
  },
})

const errorStyle = css({
  flex: 1,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  height: 160,
  fontSize: 36,
  color: 'red',
  paddingBottom: 36,
})

export const SearchBar = ({ locale }) => {
  const { t } = useTranslation(locale)
  const [documents, setDocuments] = useState()
  const [query, setQuery] = useState()
  const inputRef = useRef(null)

  const autocompleteSearch = async query => {
    if (query.length) {
      let documents = await getDocuments({
        websiteId: globoWebsiteId,
        locale,
        search: query,
        fields: ['_id', 'slug', 'title'],
      })

      documents = documents.map(document => ({
        ...document,
        url: `/docs/${locale}/${document.slug}/`,
      }))

      setDocuments(documents)
    }
  }

  const autocompleteSearchThrottled = throttle(500, autocompleteSearch)
  const autocompleteSearchDebounced = debounce(500, autocompleteSearch)

  const onCancel = () => {
    setQuery('')
    setDocuments([])
  }

  const onChange = async event => {
    let query = event.target.value.toLowerCase()

    if (query.length) {
      setQuery(query)

      if (query.length < 4) {
        autocompleteSearchThrottled(query)
      } else {
        autocompleteSearchDebounced(query)
      }
    } else {
      setQuery(query)
      setDocuments([])
    }
  }

  const onClickOutside = event => {
    if (inputRef.current && !inputRef.current.contains(event.target)) {
      setDocuments([])
    }
  }

  const onKeyDown = event => {
    if (event.key === 'Escape') {
      setQuery('')
      setDocuments([])
    }
  }

  useEffect(() => {
    document.addEventListener('mousedown', onClickOutside)

    return () => {
      document.removeEventListener('mousedown', onClickOutside)
    }
  })

  return (
    <label ref={inputRef} css={searchBar}>
      <IconSearch
        width={24}
        height={24}
        css={{ fill: 'var(--color-primary)' }}
      />

      <input
        value={query || ''}
        placeholder={t('Search documentation')}
        css={searchInput}
        onChange={onChange}
        onFocus={onChange}
        onKeyDown={onKeyDown}
      />

      <IconClose
        height={20}
        width={20}
        css={{ opacity: query ? 0.5 : 0, cursor: 'pointer' }}
        onClick={onCancel}
      />

      {documents && (
        <div
          css={{
            boxShadow:
              'rgba(0, 0, 0, 0.24) 0px 1.5px 4px, rgba(0, 0, 0, 0.19) 0px 0.5px 2px',
            width: '100%',
            left: 0,
            top: 60,
            overflowY: 'scroll',
            position: 'absolute',
            cursor: 'pointer',
            maxWidth: 400,
            maxHeight: 400,
            backgroundColor: 'white',
          }}
        >
          {documents.map(({ _id, title, url }) => (
            <a
              key={_id}
              href={url}
              css={{
                display: 'block',
                padding: 15,
                ':hover': {
                  backgroundColor: '#c2e9f7',
                },
              }}
            >
              {title}
            </a>
          ))}
        </div>
      )}
    </label>
  )
}

export const Head = ({ locale }) => {
  const { t } = useTranslation(locale)

  const title = t('Documentation')
  const description = t('Waiterio Restaurant POS')
  const canonical = `https://www.waiterio.com/docs/${locale}/`

  return (
    <>
      <title>{`${title} | ${description}`}</title>
      <link rel="canonical" href={canonical} />
      <meta name="description" content={description} />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={faviconUrl} />
      <meta property="og:site_name" content={title} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content="article" />
      <meta property="og:url" content="https://www.waiterio.com/docs/" />
      <meta property="og:image" content={faviconUrl} />
      <meta itemProp="name" content={title} />
      <meta itemProp="description" content={description} />
      <meta itemProp="image" content={faviconUrl} />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'http://schema.org/',
            '@type': 'WebPage',
            '@id': `https://www.waiterio.com/${locale}/`,
            url: `https://www.waiterio.com/${locale}/`,
            inLanguage: locale,
            isPartOf: {
              '@id': `https://www.waiterio.com/${locale}/#website`,
            },
            author: {
              '@id': 'https://www.waiterio.com/#corporation',
            },
            publisher: {
              '@id': 'https://www.waiterio.com/#corporation',
            },
            translationOfWork: {
              '@id': 'https://www.waiterio.com/en/#website',
            },
            '@graph': [
              {
                '@type': 'WebSite',
                '@id': `https://www.waiterio.com/${locale}/#website`,
                name: 'Waiterio',
                url: `https://www.waiterio.com/${locale}/#website`,
                inLanguage: locale,
                author: {
                  '@id': 'https://www.waiterio.com/#corporation',
                },
                publisher: {
                  '@id': 'https://www.waiterio.com/#corporation',
                },
              },
              {
                '@type': 'Corporation',
                '@id': 'https://www.waiterio.com/#corporation',
                name: 'Waiterio',
                url: 'https://www.waiterio.com/#corporation',
                sameAs: [
                  'https://www.facebook.com/waiterio',
                  'https://www.linkedin.com/company/waiter-io',
                ],
              },
            ],
          }),
        }}
      />
    </>
  )
}

export const paths = languages.map(locale => ({
  path: `/docs/${locale}/`,
  props: {
    locale,
  },
}))

const DocumentsPage = ({ locale }) => {
  const { t } = useTranslation(locale)
  let {
    data: documents,
    error,
    loading,
  } = useDocuments({
    websiteId: globoWebsiteId,
    locale,
    fields: ['_id', 'slug', 'title'],
  })

  documents = documents?.map(document => ({
    ...document,
    url: `/docs/${locale}/${document.slug}/`,
  }))

  return (
    <Layout locale={locale}>
      <div css={jumbotron}>
        <h1 css={{ color: 'white', padding: '30px 0px' }}>
          {t('Documentation')}
        </h1>
        <SearchBar locale={locale} />
      </div>

      {documents && (
        <div css={{ margin: '15px 30px' }}>
          {documents.map(({ _id, url, title }) => (
            <Link key={_id} css={documentLink} to={url}>
              {title}
            </Link>
          ))}
        </div>
      )}

      {loading && <div style={{ marginBottom: 30 }} className="loader" />}

      {error && <div css={errorStyle}>{error}</div>}
    </Layout>
  )
}

export default DocumentsPage
